/**
 * DataTable Component
 *
 * Comprehensive data display component that combines:
 * - Toolbar with filters and view toggle
 * - Results summary and pagination controls
 * - Data view (cards or list with headers)
 *
 * This is the recommended component for displaying tabular/card data.
 */
import React from "react";
import PropTypes from "prop-types";
import { FaSpinner } from "react-icons/fa";
import { DataView } from "../DataView";
import "./DataTable.scss";

export const DataTable = ({
  // Toolbar props
  toolbar,
  utilityBar,

  // View mode
  viewMode = "list",
  onViewModeChange,

  // Data and columns
  children,
  columns,

  // Pagination
  currentPage,
  totalPages,
  onPageChange,
  perPage,
  onPerPageChange,
  totalItems,
  itemName = "item",

  // Loading state
  loading,
  loadingMore,

  // Empty state
  empty,
  emptyIcon: EmptyIcon,
  emptyTitle,
  emptyMessage,

  className = "",
}) => {
  // Use provided empty prop if available, otherwise calculate it
  const isEmpty =
    empty !== undefined
      ? empty
      : !loading && React.Children.count(children) === 0;

  // Calculate display range
  const startItem = ((currentPage || 1) - 1) * (perPage || 25) + 1;
  const endItem = Math.min(
    (currentPage || 1) * (perPage || 25),
    totalItems || 0
  );

  // Smart pluralization
  const getPlural = (name, count) => {
    if (count === 1) return name;
    // Handle common irregular plurals and words ending in 'y'
    if (
      name.endsWith("y") &&
      !["ay", "ey", "iy", "oy", "uy"].some((ending) => name.endsWith(ending))
    ) {
      return name.slice(0, -1) + "ies";
    }
    return `${name}s`;
  };
  const itemNamePlural = getPlural(itemName, totalItems);

  return (
    <div className={`data-table ${className}`}>
      {/* Toolbar */}
      {toolbar && <div className="data-table__toolbar">{toolbar}</div>}

      {/* Utility Bar - Secondary row for action buttons */}
      {utilityBar && (
        <div className="data-table__utility-bar">{utilityBar}</div>
      )}

      {/* Results Summary */}
      {!isEmpty && (
        <div className="data-table__summary">
          <span className="data-table__summary-text">
            Showing {startItem}-{endItem} of {totalItems} {itemNamePlural}
          </span>

          {onPerPageChange && (
            <div className="data-table__per-page">
              <label>Per page:</label>
              <select
                value={perPage}
                onChange={(e) => onPerPageChange(Number(e.target.value))}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Data View */}
      {loading ? (
        <div className="data-table__loading">
          <FaSpinner className="data-table__loading-spinner" />
          <p className="data-table__loading-message">Loading {itemNamePlural}...</p>
        </div>
      ) : isEmpty ? (
        <div className="data-table__empty">
          {EmptyIcon && <EmptyIcon className="data-table__empty-icon" />}
          {emptyTitle && <h3>{emptyTitle}</h3>}
          {emptyMessage && <p>{emptyMessage}</p>}
        </div>
      ) : (
        <DataView
          viewMode={viewMode}
          columns={columns}
        >
          {children}
        </DataView>
      )}

      {/* Pagination */}
      {!isEmpty && totalPages > 1 && (
        <div className="data-table__pagination">
          <button
            className="data-table__page-btn"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            className="data-table__page-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="data-table__page-info">
            Page {currentPage} of {totalPages}
            {loadingMore && (
              <span style={{ marginLeft: "0.5rem", color: "#8b5cf6" }}>
                <svg
                  style={{
                    display: "inline-block",
                    width: "1rem",
                    height: "1rem",
                    animation: "spin 1s linear infinite",
                  }}
                  viewBox="0 0 24 24"
                >
                  <circle
                    style={{ opacity: 0.25 }}
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    style={{ opacity: 0.75 }}
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            )}
          </span>
          <button
            className="data-table__page-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
          <button
            className="data-table__page-btn"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};

DataTable.propTypes = {
  toolbar: PropTypes.node,
  utilityBar: PropTypes.node,
  // Retained for call-site compatibility; card view has been retired and
  // only "list" is rendered. See useViewMode.
  viewMode: PropTypes.oneOf(["cards", "list"]),
  onViewModeChange: PropTypes.func,
  children: PropTypes.node,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      width: PropTypes.string,
      align: PropTypes.oneOf(["left", "center", "right"]),
    })
  ),
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  perPage: PropTypes.number,
  onPerPageChange: PropTypes.func,
  totalItems: PropTypes.number.isRequired,
  itemName: PropTypes.string,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  empty: PropTypes.bool,
  emptyIcon: PropTypes.elementType,
  emptyTitle: PropTypes.string,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
};
